import { formatUsPhone, queryKeys, usPhoneSchema } from '@mevaser/shared'
import { type QueryClient, useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.js'

export interface CallerMatch {
  id: string
  name: string
}

/** The saved customer who owns this number (primary or extra), or null. Shares
 *  the `/customers/match` cache entry the support-ticket screens use. */
export const callerMatchOptions = (raw: string | null) => {
  const parsed = raw ? usPhoneSchema.safeParse(raw) : null
  const phone = parsed?.success ? parsed.data : ''
  return {
    queryKey: queryKeys.customers.match(phone),
    queryFn: () =>
      api.get<{ customer: CallerMatch | null }>('/customers/match', { query: { phone } }),
    enabled: Boolean(phone),
    staleTime: 5 * 60_000,
    retry: false,
  }
}

export function useCallerMatch(raw: string | null): CallerMatch | null {
  const { data } = useQuery(callerMatchOptions(raw))
  return data?.customer ?? null
}

export function formatCallerNumber(raw: string | null): string {
  if (!raw) return 'Unknown number'
  try {
    return formatUsPhone(raw)
  } catch {
    return raw
  }
}

/** "Name · (555) 123-4567" for a desktop notification; the plain number when
 *  there is no match, the lookup fails, or it takes over ~1.5 s. */
export async function callerNotificationLabel(
  queryClient: QueryClient,
  raw: string | null,
): Promise<string> {
  const fallback = raw ?? 'Unknown number'
  const options = callerMatchOptions(raw)
  if (!options.enabled) return fallback
  try {
    const result = await Promise.race([
      queryClient.fetchQuery(options),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500)),
    ])
    return result?.customer ? `${result.customer.name} · ${formatCallerNumber(raw)}` : fallback
  } catch {
    return fallback
  }
}
